import { Children, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { SectionHeading } from "~/components/SectionHeading";
import type { ArtistHit, Found, GenreTally, GigHit, VenueTally } from "~/api/types";
import { attempt } from "~/api/attempt";
import { api } from "~/api/client";
import { bubbleFor } from "~/components/bubbles";
import { AppLink } from "~/components/AppLink";
import { FoldableSection } from "~/components/FoldableSection";
import type { MarksActions } from "~/components/MarksContext";
import { MarksProvider } from "~/components/MarksContext";
import { look } from "~/components/Search";
import { useViewer } from "~/Viewer";

import s from "./Results.module.css";

/**
 * Everything matching, on a page of its own.
 *
 * The bar's dropdown is the first few of each kind. That is right when you already know
 * what you are after and useless when you are browsing — "what is on at Corsica" wants a
 * list, not a peek at three of them. So Enter brings you here, and here is the whole answer.
 *
 * It shares `look` with the box rather than fetching its own way, so the two can never
 * disagree about what matches. They differ in how much they draw, which is the only thing
 * they should differ in.
 */

interface Found5 {
  events: GigHit[];
  artists: ArtistHit[];
  venues: VenueTally[];
  genres: GenreTally[];
  people: Found[];
}

/** How many rows a section shows before it asks. */
const FEW = 5;

/**
 * A section of rows, folded to a handful until somebody wants the rest.
 *
 * A search for a common word answers with a long column of acts, and every section under it
 * — venues, genres, people — is pushed off the bottom of the screen. Measured on "jungle":
 * Genres began at y=1292 in an 813px window, so the only way to learn those sections existed
 * was to scroll past nine artists first.
 *
 * The count in the heading is the whole number throughout, never the number on screen. It is
 * what the section found, and a heading that counted the fold would be answering a question
 * about this page rather than about the search.
 */
function Section({
  title,
  n,
  children,
}: {
  title: string;
  n: number;
  children: React.ReactNode;
}) {
  const [all, setAll] = useState(false);
  if (n === 0) return null;
  const rows = Children.toArray(children);
  const shown = all ? rows : rows.slice(0, FEW);
  const hidden = rows.length - shown.length;
  return (
    <section>
      <div className={s.head}>
        <SectionHeading>{title}</SectionHeading>
        <span className={s.meta}>{n}</span>
      </div>
      <ul className={s.rows}>{shown}</ul>
      {hidden > 0 ? (
        <button type="button" className={s.more} onClick={() => setAll(true)}>
          {`+${hidden} more`}
        </button>
      ) : null}
    </section>
  );
}

export function Results() {
  const [params] = useSearchParams();
  const me = useViewer();
  const asked = (params.get("q") ?? "").trim();
  const [found, setFound] = useState<Found5 | null>(null);

  useEffect(() => {
    if (!asked) {
      setFound(null);
      return;
    }
    let live = true;
    setFound(null);
    void look(asked, Boolean(me), 60).then((hits) => {
      if (live) setFound(hits);
    });
    return () => {
      live = false;
    };
  }, [asked, me]);

  const total = found
    ? found.events.length +
      found.artists.length +
      found.venues.length +
      found.genres.length +
      found.people.length
    : 0;

  /*
   * The marks, which this page had none of.
   *
   * A gig here is the same `EventCard` the front page draws, and the card asks context for
   * its heart rather than taking it as a prop — so a page that renders cards without a
   * provider renders them with no controls at all. That is what happened: search results
   * were gigs you could look at and not act on, and the card gave no sign it had been
   * stripped of anything.
   *
   * The patch is this page's own, for the same reason Highlights keeps its own: the results
   * are a list this page fetched, and routing a heart through some other view's state would
   * post successfully and change nothing on screen.
   */
  const mark = (id: string, patch: (e: GigHit) => GigHit) =>
    setFound((was) =>
      was === null
        ? was
        : {
            ...was,
            events: was.events.map((e) => (e.id === id ? patch(e) : e)),
          },
    );

  // Patched first, written second, and put back if the write does not take — see the note
  // on `acting` in `Highlights.tsx`, which this follows.
  const markAct = (artistId: number, on: boolean) =>
    setFound((was) =>
      was === null
        ? was
        : {
            ...was,
            events: was.events.map((e) =>
              e.act?.id === artistId
                ? { ...e, starred: on, picked: on || Boolean(e.picked) }
                : e,
            ),
          },
    );
  const acting: MarksActions = {
    me: me ?? null,
    save: (id, on) => {
      mark(id, (e) => ({ ...e, liked: on }));
      void attempt(() => api.save(id, on)).then((took) => {
        if (!took) mark(id, (e) => ({ ...e, liked: !on }));
      });
    },
    going: (id, on) => {
      // Saying you are going puts it on the list; unsaying it does not take it off.
      mark(id, (e) => ({ ...e, going: on, liked: on || Boolean(e.liked) }));
      void attempt(() => api.going(id, on)).then((took) => {
        if (!took) mark(id, (e) => ({ ...e, going: !on }));
      });
    },
    star: (artistId, on) => {
      markAct(artistId, on);
      void attempt(() => api.star(artistId, on)).then((took) => {
        if (!took) markAct(artistId, !on);
      });
    },
  };

  return (
    <MarksProvider value={acting}>
      <main className={s.wrap}>
        <header className={s.top}>
          <h1 className={s.name}>{asked ? `“${asked}”` : "Search"}</h1>
          <p className={s.said}>
            {!asked
              ? "Type in the bar above to look for a gig, an act, a room or somebody you know."
              : found === null
                ? "Looking…"
                : total === 0
                  ? "Nothing matches that."
                  : `${total} ${total === 1 ? "result" : "results"}`}
          </p>
        </header>

        {found ? (
          <>
            {/*
             * Gigs are cards, folded the way the front page folds them — the same component,
             * so a gig looks the same wherever you meet one and "show more" behaves the same
             * way. The other three kinds are not gigs and have no card; they stay as rows.
             */}
            {found.events.length > 0 ? (
              <FoldableSection
                id="gigs"
                heading="Gigs"
                meta={
                  // A bare count, like the three row sections below it. This said "17
                  // matches" while Acts said "9", so one page counted the same kind of
                  // thing two ways — and the heading beside it carries the word either way.
                  String(found.events.length)
                }
                events={found.events}
                hasMore={false}
                moreHref="/calendar"
                doneText="that is everything matching"
                eager
                flush
              />
            ) : null}

            {/* A link, like the rooms and the people below it. It was the one row on this
              page drawn as a button, which looks the same and is not: a button cannot be
              opened in a new tab, cmd-clicked, copied, or read by anything that collects
              the links on a page. An act is an address — `/a/<id>` is a real page that a
              cold visit opens — so it should be written as one. */}
            <Section title="Acts" n={found.artists.length}>
              {found.artists.map((a) => (
                <li key={a.id}>
                  <AppLink className={s.row ?? ""} to={`/a/${a.id}`}>
                    {/* The picture sits in the first column, which is the one the rooms and
                        the people below use for a count — so the three row sections line up
                        with each other. It used to say "the slot the gigs use for a date",
                        which stopped being true when the gigs became cards.

                        A bubble where there is no photograph, which is five acts in six —
                        the slot was empty on those, so a search for a club night returned a
                        page of names with nothing in the first column. */}
                    <span className={s.when}>
                      <img
                        className={s.face}
                        src={a.portrait ?? bubbleFor(`a-${a.id}`)}
                        alt=""
                        loading="lazy"
                        decoding="async"
                      />
                    </span>
                    <span className={s.what}>
                      <b>
                        {a.is_favourite ? "★ " : ""}
                        {a.name}
                      </b>
                    </span>
                    <span className={s.where}>
                      {a.appearances} {a.appearances === 1 ? "date" : "dates"}
                    </span>
                  </AppLink>
                </li>
              ))}
            </Section>

            <Section title="Venues" n={found.venues.length}>
              {found.venues.map((v) => (
                <li key={v.slug}>
                  <AppLink className={s.row ?? ""} to={`/v/${v.slug}`}>
                    <span className={s.when} />
                    <span className={s.what}>
                      <b>
                        {v.tracked ? "★ " : ""}
                        {v.name}
                      </b>
                    </span>
                    <span className={s.where}>
                      {v.count} {v.count === 1 ? "gig" : "gigs"}
                    </span>
                  </AppLink>
                </li>
              ))}
            </Section>

            {/* Below the rooms and above the people. A genre is a broader answer than
                either, and somebody typing a name wants the name first. */}
            <Section title="Genres" n={found.genres.length}>
              {found.genres.map((g) => (
                <li key={g.genre}>
                  <AppLink
                    className={s.row ?? ""}
                    to={`/g/${encodeURIComponent(g.genre)}`}
                  >
                    <span className={s.when} />
                    <span className={s.what}>
                      <b>{g.genre}</b>
                    </span>
                    <span className={s.where}>
                      {g.acts} {g.acts === 1 ? "act" : "acts"}
                    </span>
                  </AppLink>
                </li>
              ))}
            </Section>

            <Section title="People" n={found.people.length}>
              {found.people.map((p) => (
                <li key={p.id}>
                  <AppLink className={s.row ?? ""} to={`/u/${p.username}`}>
                    <span className={s.when} />
                    <span className={s.what}>
                      <b>{p.name}</b>
                    </span>
                    <span className={s.where}>@{p.username}</span>
                  </AppLink>
                </li>
              ))}
            </Section>
          </>
        ) : null}
      </main>
    </MarksProvider>
  );
}
